import { useState } from "react";
import { shortDate, money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import SearchBar from "@/components/common/SearchBar";
import { btn } from "@/components/common/btn";
import { STATUS_COLORS, STATUS_LABELS } from "./workOrderTabUtils";

// Top-of-tab list of all active work orders for a client. Each row
// links into the WO editor via `openEdit(wo)`; draft rows expose
// an inline delete. Extracted from WorkOrderTab.jsx (Feb 2026,
// iter182). Note: the parent intentionally killed the standalone
// "+ New Work Order" button (Apr 2026 spec): WOs can only be born
// from Equipment or an approved Proposal.
export function WOListTable({
  activeWorkOrders,
  openEdit,
  getSiteNameById,
  technicians,
  fmt,
  handleDelete,
  onNew,
}) {
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const rows = q
    ? activeWorkOrders.filter((wo) => [wo.wo_number, wo.title, getSiteNameById(wo.site_id), STATUS_LABELS[wo.status] || wo.status, wo.customer_po_number]
        .some((v) => (v || "").toLowerCase().includes(q)))
    : activeWorkOrders;
  return (
    <div className="space-y-3" data-testid="work-orders-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <SearchBar value={search} onChange={setSearch} placeholder="Search work orders..." testId="search-work-orders" />
        {onNew && (
          <button type="button" className={`${btn("write")} w-7 justify-center !px-0`} onClick={onNew} data-testid="btn-add-customer-work-order" title="New work order for this customer" aria-label="New work order">
            <Plus className="w-3.5 h-3.5" />
          </button>
        )}
        <span className="ml-auto text-xs text-slate-500 num">{rows.length} active</span>
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm">{q ? "No work orders match that search" : "No active work orders"}</div>
      ) : (
        <div className="data-table-container overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="th-std w-20">WO #</TableHead>
                <TableHead className="th-std">Title</TableHead>
                <TableHead className="th-std w-48">Site</TableHead>
                <TableHead className="th-std w-20">Date</TableHead>
                <TableHead className="th-std w-20">Scheduled</TableHead>
                <TableHead className="th-std w-24">Tech</TableHead>
                <TableHead className="th-std w-24 text-right">Total</TableHead>
                <TableHead className="th-std w-20">Status</TableHead>
                <TableHead className="th-std w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((wo) => {
                const techIds = wo.assigned_tech_ids?.length
                  ? wo.assigned_tech_ids
                  : wo.assigned_tech_id
                    ? [wo.assigned_tech_id]
                    : [];
                return (
                  <TableRow key={wo.id} className="table-row-hover cursor-pointer" onClick={() => openEdit(wo)} data-testid={`wo-row-${wo.id}`}>
                    <TableCell className="text-xs">
                      <button
                        className="text-xs font-mono font-medium text-brand hover:underline cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); openEdit(wo); }}
                        data-testid={`wo-link-${wo.id}`}
                      >
                        {wo.wo_number || "-"}
                      </button>
                    </TableCell>
                    <TableCell className="text-xs text-slate-800">
                      {wo.title}
                      {wo.annual_service && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full border border-slate-200 text-slate-500 font-medium">
                          Annual
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 break-words">
                      {getSiteNameById(wo.site_id) || "-"}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 tabular-nums">
                      {shortDate(wo.work_date || wo.created_at)}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 tabular-nums">
                      {shortDate(wo.scheduled_date)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {techIds.length === 0 ? (
                        <span className="text-slate-400">-</span>
                      ) : (
                        <span className="flex items-center gap-1.5">
                          {techIds.map((tid) => {
                            const tech = technicians.find((t) => t.id === tid);
                            return tech ? (
                              <span key={tid} className="flex items-center gap-1">
                                <span
                                  className="w-2 h-2 rounded-full inline-block"
                                  style={{ backgroundColor: tech.color }}
                                />
                                {tech.name}
                              </span>
                            ) : null;
                          })}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-medium num">{money(wo.total)}</TableCell>
                    <TableCell className="text-xs">
                      <span
                        className={`inline-flex w-[88px] justify-center text-[10px] px-2 py-0.5 rounded-md font-semibold whitespace-nowrap ${
                          STATUS_COLORS[wo.status] || "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {STATUS_LABELS[wo.status] || wo.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">
                      {wo.status === "draft" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                          onClick={(e) => { e.stopPropagation(); handleDelete(wo.id); }}
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
